import React, { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Navbar from '../components/navbar';
import Footer from '../components/footer';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebaseConfig';
import { Helmet, HelmetProvider } from '@dr.pogodin/react-helmet';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { talkImageMode } from '../sectionLayout';
import '../css/TalkPage.css';

// Merge multiple HTML snippets into one, normalize lists, and stitch adjacent lists together
function mergeAndNormalizeHTML(htmlParts) {
    const container = document.createElement('div');

    // Append all parts as DOM nodes (no separators)
    htmlParts.forEach((html) => {
        if (!html) return;
        const tmp = document.createElement('div');
        tmp.innerHTML = html;
        while (tmp.firstChild) container.appendChild(tmp.firstChild);
    });

    // Reconstruct lists from Quill's per-<li> data attributes so mixed lists render correctly
    //  - Quill often wraps everything in a single <ol> and uses data-list="bullet"/"ordered"
    //    and classes like ql-indent-N on <li>. We rebuild proper <ul>/<ol> structure.

    // Preserve empty paragraphs (<p><br></p>) as visual gaps we can style later
    Array.from(container.querySelectorAll('p')).forEach((p) => {
        const onlyBr = p.children.length === 1 && p.firstElementChild?.tagName === 'BR';
        const isEmpty = p.textContent.trim() === '';
        if (onlyBr || isEmpty) {
            const gap = document.createElement('div');
            gap.className = 'section-gap';
            gap.setAttribute('data-lines', '1');
            p.replaceWith(gap);
        }
    });

    // Merge consecutive gap nodes and set a temporary inline height so gaps are visible before CSS pass
    (function mergeGaps(root) {
        let node = root.firstChild;
        while (node) {
            if (node.nodeType === 1 && node.classList.contains('section-gap')) {
                let lines = 1;
                let next = node.nextSibling;
                while (next && next.nodeType === 1 && next.classList?.contains('section-gap')) {
                    lines += parseInt(next.getAttribute('data-lines') || '1', 10);
                    const toRemove = next;
                    next = next.nextSibling;
                    toRemove.remove();
                }
                node.setAttribute('data-lines', String(lines));
                // Temporary inline style; we can replace with CSS later
                node.style.display = 'block';
                node.style.height = (lines * 14) + 'px';
            }
            node = node.nextSibling;
        }
    })(container);

    function buildList(tag) {
        return document.createElement(tag); // 'ul' or 'ol'
    }

    function liLevel(li) {
        const m = (li.className || '').match(/ql-indent-(\d+)/);
        return m ? parseInt(m[1], 10) : 0;
    }

    function liType(li, parentTag) {
        const t = li.getAttribute('data-ql-list') || li.getAttribute('data-list');
        if (t === 'bullet') return 'ul';
        if (t === 'ordered') return 'ol';
        // Fallback to parent tag if not specified
        return parentTag.toLowerCase();
    }

    function cleanLi(li) {
        // clone to avoid moving nodes while iterating source list
        const newLi = document.createElement('li');
        // strip Quill control spans and attributes
        Array.from(li.childNodes).forEach((child) => {
            if (child.nodeType === 1 && child.classList.contains('ql-ui')) return; // drop
            newLi.appendChild(child.cloneNode(true));
        });
        newLi.removeAttribute('data-ql-list');
        newLi.removeAttribute('data-list');
        // remove ql-indent-* from li itself
        newLi.className = (li.className || '').replace(/\bql-indent-\d+\b/g, '').trim();
        return newLi;
    }

    function reconstructList(listNode) {
        const parentTag = listNode.tagName.toLowerCase();
        const lis = Array.from(listNode.querySelectorAll(':scope > li'));
        const frag = document.createDocumentFragment();
        // stack entries: {node, level, type}
        const stack = [{ node: frag, level: -1, type: null }];

        lis.forEach((li) => {
            const level = liLevel(li);
            const type = liType(li, parentTag); // 'ul' or 'ol'

            // pop to the correct level
            while (stack[stack.length - 1].level >= level) stack.pop();

            // ensure a list of the right type exists at this level
            let top = stack[stack.length - 1];
            let currentList = top.node.lastElementChild;
            if (!currentList || currentList.tagName.toLowerCase() !== type) {
                currentList = buildList(type);
                if (listNode.className) currentList.className = listNode.className; // carry styling
                top.node.appendChild(currentList);
            }

            // push this list as the new top for potential children
            stack.push({ node: currentList, level, type });
            currentList.appendChild(cleanLi(li));
        });

        listNode.replaceWith(frag);
    }

    // Snapshot lists first because we mutate the DOM as we go
    Array.from(container.querySelectorAll('ol, ul')).forEach((list) => reconstructList(list));

    // Merge adjacent lists of the same type so they appear seamless
    let node = container.firstChild;
    while (node) {
        const next = node.nextSibling;
        if (
            next &&
            node.nodeType === 1 &&
            next.nodeType === 1 &&
            (node.tagName === 'UL' || node.tagName === 'OL') &&
            next.tagName === node.tagName
        ) {
            while (next.firstChild) node.appendChild(next.firstChild);
            next.remove();
            // do not advance; there may be another list following
            continue;
        }
        node = next;
    }

    return container.innerHTML;
}

// Permission helpers (mirrors chapter dashboard)
const roleRank = { publisher: 3, deacon: 2, writer: 1 };
const getRank = (r) => roleRank[(r || "").toLowerCase()] || 0;
const canEditByStatusAndRole = (status, roleName) => {
    const s = (status || "").toLowerCase();
    const rank = getRank(roleName);
    // Draft and Ready for Deacons: anyone (writer, deacon, publisher)
    if (s === "draft" || s === "ready for deacons") return rank >= 1;
    // Ready to Publish: only deacon and publisher
    if (s === "ready to publish" || s === "ready for publish" || s === "ready for publisher") return rank >= 2;
    // Published: only publisher
    if (s === "published") return rank >= 3;
    return false;
};

export default function TalkPage() {
    const { talkId } = useParams();
    const navigate = useNavigate();
    const [talk, setTalk] = useState(null);
    const [loading, setLoading] = useState(true);
    const [isMobile, setIsMobile] = useState(false);
    const [pageLoaded, setPageLoaded] = useState(false);
    const [currentUser, setCurrentUser] = useState(null);
    const [role, setRole] = useState(null);
    const normalizedRole = role?.toLowerCase();
    const [canEdit, setCanEdit] = useState(false);
    const mainRef = useRef(null);
    useEffect(() => {
        const id = requestAnimationFrame(() => setPageLoaded(true));
        return () => cancelAnimationFrame(id);
    }, []);

    // Wire up footnote <-> reference jumping. The body has bare <sup>N</sup>
    // markers and the References section is an ordered list; we link them both
    // ways and smooth-scroll + flash the target on click. Runs idempotently and
    // re-applies itself if React re-renders the body HTML.
    useEffect(() => {
        if (!talk) return undefined;

        let observer = null;
        let rafId = 0;
        let tries = 0;

        const linkFootnotes = () => {
            const body = mainRef.current && mainRef.current.querySelector('.talk-body');
            if (!body) return false;

            const refList = body.querySelector('.talk-references .section-content ol');
            const refItems = refList ? Array.from(refList.querySelectorAll(':scope > li')) : [];
            if (refItems.length === 0) return true;

            if (observer) observer.disconnect(); // don't observe our own edits

            const contentAreas = Array.from(body.querySelectorAll('.section-content'))
                .filter((el) => !el.closest('.talk-references'));

            const citeCount = {};
            contentAreas.forEach((area) => {
                area.querySelectorAll('sup').forEach((sup) => {
                    const num = parseInt((sup.textContent || '').trim().replace(/[^\d].*$/, ''), 10);
                    if (!Number.isFinite(num) || num < 1 || num > refItems.length) return;
                    citeCount[num] = (citeCount[num] || 0) + 1;
                    if (sup.dataset.fnDone) return;

                    const occ = citeCount[num];
                    sup.dataset.fnDone = '1';
                    sup.classList.add('footnote-ref');
                    sup.id = occ === 1 ? `fnref-${num}` : `fnref-${num}-${occ}`;

                    const a = document.createElement('a');
                    a.href = `#ref-${num}`;
                    a.className = 'footnote-ref-link';
                    a.setAttribute('aria-label', `Jump to reference ${num}`);
                    while (sup.firstChild) a.appendChild(sup.firstChild);
                    sup.appendChild(a);
                });
            });

            refItems.forEach((li, i) => {
                const num = i + 1;
                li.id = `ref-${num}`;
                li.classList.add('reference-item');
                if (li.querySelector(':scope > .footnote-backrefs')) return;
                const count = citeCount[num] || 0;
                if (count === 0) return;

                const wrap = document.createElement('span');
                wrap.className = 'footnote-backrefs';
                for (let occ = 1; occ <= count; occ += 1) {
                    const targetId = occ === 1 ? `fnref-${num}` : `fnref-${num}-${occ}`;
                    const a = document.createElement('a');
                    a.href = `#${targetId}`;
                    a.className = 'footnote-backref';
                    a.textContent = count > 1 ? `↑${String.fromCharCode(96 + occ)}` : '↑';
                    a.setAttribute('aria-label', `Back to citation ${num}`);
                    wrap.appendChild(a);
                }
                const qlui = li.querySelector(':scope > .ql-ui');
                if (qlui && qlui.nextSibling) li.insertBefore(wrap, qlui.nextSibling);
                else if (qlui) li.appendChild(wrap);
                else li.insertBefore(wrap, li.firstChild);
            });

            body.dataset.footnotesLinked = String(Object.keys(citeCount).length);
            if (observer) observer.observe(body, { childList: true, subtree: true });
            return true;
        };

        // Keep trying until the body (with its dangerouslySetInnerHTML content) exists.
        const attempt = () => {
            if (linkFootnotes()) {
                const body = mainRef.current && mainRef.current.querySelector('.talk-body');
                if (body && !observer) {
                    observer = new MutationObserver(() => linkFootnotes());
                    observer.observe(body, { childList: true, subtree: true });
                }
                return;
            }
            if (tries < 30) {
                tries += 1;
                rafId = requestAnimationFrame(attempt);
            }
        };
        attempt();

        const flash = (el) => {
            el.classList.remove('footnote-flash');
            // eslint-disable-next-line no-unused-expressions
            el.offsetWidth; // restart the CSS animation
            el.classList.add('footnote-flash');
        };

        const onClick = (e) => {
            const link = e.target.closest('a.footnote-ref-link, a.footnote-backref');
            if (!link) return;
            const hash = link.getAttribute('href') || '';
            if (!hash.startsWith('#')) return;
            const target = document.getElementById(hash.slice(1));
            if (!target) return;
            e.preventDefault();
            const rect = target.getBoundingClientRect();
            const mid = window.scrollY + rect.top - Math.max(0, (window.innerHeight - rect.height) / 2);
            const top = Math.max(0, Math.round(mid));
            try { window.scrollTo({ top, behavior: 'smooth' }); } catch (_) { window.scrollTo(0, top); }
            flash(target);
            try { window.history.replaceState(null, '', hash); } catch (_) { /* ignore */ }
        };
        document.addEventListener('click', onClick);

        return () => {
            cancelAnimationFrame(rafId);
            if (observer) observer.disconnect();
            document.removeEventListener('click', onClick);
        };
    }, [talk]);

    useEffect(() => {
        const auth = getAuth();
        const unsubscribe = onAuthStateChanged(auth, async (user) => {
            setCurrentUser(user || null);

            if (!user) {
                setRole(null);
                return;
            }

            try {
                const userRef = doc(db, "users", user.uid);
                const userSnap = await getDoc(userRef);
                setRole(userSnap.exists() ? userSnap.data().role : null);
            } catch (e) {
                console.error("Error loading user role:", e);
                setRole(null);
            }
        });
        return () => unsubscribe();
    }, []);

    useEffect(() => {
        setCanEdit(canEditByStatusAndRole(talk?.status, normalizedRole));
    }, [talk, normalizedRole]);

    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth <= 768);
        };
        handleResize();
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        async function fetchTalk() {
            try {
                const snap = await getDoc(doc(db, 'talks', talkId));
                if (snap.exists()) {
                    setTalk(snap.data());
                }
            } catch (err) {
                console.error('Error fetching talk:', err);
            } finally {
                setLoading(false);
            }
        }
        fetchTalk();
    }, [talkId]);

    if (loading) {
        return <div className="talk-page loading">Loading...</div>;
    }
    if (!talk) {
        return <div className="talk-page error">Talk not found.</div>;
    }

    const verseText = (talk.verseText || "").trim();
    const verseRef = (talk.verseRef || "").trim();
    const hasReferences = typeof talk.references === "string" && talk.references.trim() !== "";
    const hasBody = (Array.isArray(talk.sections) && talk.sections.length > 0) || hasReferences;

    return (
        <div className={`talk-css ${pageLoaded ? 'is-loaded' : ''}`}>
            <Helmet>
                <title>{talk.title}</title>
            </Helmet>
            <Navbar />
            <main className="talk-page" ref={mainRef}>
                <section className="talk-hero">
                    <h1 className="talk-title">{talk.title}</h1>
                    <div className="talk-cta-row">
                        {!isMobile && (
                            <>
                                <button
                                    className="btn slideshow-btn"
                                    onClick={() => navigate(`/slideshow/${talkId}`)}
                                >
                                    View as Slideshow
                                </button>
                                <button
                                    className="btn download-pdf-btn"
                                    onClick={() => window.open(`/print/${talkId}`, "_blank")}
                                >
                                    Download PDF
                                </button>
                                {canEdit && (
                                    <button
                                        className="btn edit-talk-btn"
                                        onClick={() => navigate(`/editor/${talkId}`)}
                                    >
                                        Edit Talk
                                    </button>
                                )}
                            </>
                        )}
                    </div>
                </section>
                {(verseText || verseRef) && (
                    <section className="talk-verse">
                        <div className="talk-verse-card">
                            {verseText && (
                                <div className="talk-verse-text">"{verseText}"</div>
                            )}
                            {verseRef && (
                                <div className="talk-verse-ref">
                                    <span className="talk-verse-reftext">{verseRef}</span>
                                </div>
                            )}
                        </div>
                    </section>
                )}
                {hasBody && (
                <div className="talk-body">
                {Array.isArray(talk.sections) &&
                    talk.sections.map((section, idx) => {
                        const isDivider = section?.type === "divider";
                        if (isDivider) {
                            return (
                                <section key={idx} className="talk-section section-divider">
                                    <h2 className="section-divider-title">{section?.header || ""}</h2>
                                </section>
                            );
                        }
                        return (() => {
                            // Build the combined HTML for any textual content (standard/hybrid)
                            const parts = [];
                            if (section?.body) parts.push(section.body);
                            if (Array.isArray(section?.bodies) && section.bodies.length > 0) {
                                section.bodies.forEach((p) => { if (p) parts.push(p); });
                            }
                            const combinedHTML = mergeAndNormalizeHTML(parts);

                            // Determine layout from the author's per-surface choice
                            // (with a fallback to the historical inference).
                            const imageUrl = (section?.imageUrl || "").trim();
                            const hasText = (combinedHTML || "").trim() !== "";
                            const mode = imageUrl ? talkImageMode(section) : "text";
                            const isGraphic = imageUrl && (mode === "image" || (mode === "both" && !hasText));
                            const isHybrid = imageUrl && mode === "both" && hasText;

                            // 1) GRAPHIC: title + large centered image
                            if (isGraphic && imageUrl) {
                                return (
                                    <section key={idx} className="talk-section section-graphic">
                                        <h2 className="section-title">{section?.header || ""}</h2>
                                        <figure className="section-graphic-figure">
                                            <img
                                                src={imageUrl}
                                                alt={section?.imageAlt || section?.header || "Section image"}
                                                className="section-graphic-img"
                                                loading="lazy"
                                            />
                                            {section?.imageCaption && (
                                                <figcaption className="section-graphic-caption">{section.imageCaption}</figcaption>
                                            )}
                                        </figure>
                                    </section>
                                );
                            }

                            // 2) HYBRID: title + two-column layout (text left, image right)
                            if (isHybrid && imageUrl) {
                                return (
                                    <section key={idx} className="talk-section section-hybrid">
                                        <h2 className="section-title">{section?.header || ""}</h2>
                                        <div className="section-hybrid-grid">
                                            <div className="section-hybrid-left">
                                                <div
                                                    className="section-content"
                                                    dangerouslySetInnerHTML={{ __html: combinedHTML }}
                                                />
                                            </div>
                                            <div className="section-hybrid-right">
                                                <figure className="section-hybrid-figure">
                                                    <img
                                                        src={imageUrl}
                                                        alt={section?.imageAlt || section?.header || "Section image"}
                                                        className="section-hybrid-img"
                                                        loading="lazy"
                                                    />
                                                    {section?.imageCaption && (
                                                        <figcaption className="section-hybrid-caption">{section.imageCaption}</figcaption>
                                                    )}
                                                </figure>
                                            </div>
                                        </div>
                                    </section>
                                );
                            }

                            // 3) STANDARD (default): title + text
                            return (
                                <section key={idx} className="talk-section section-standard">
                                    <h2 className="section-title">{section?.header || ""}</h2>
                                    <div
                                        className="section-content"
                                        dangerouslySetInnerHTML={{ __html: combinedHTML }}
                                    />
                                </section>
                            );
                        })();
                    })}
                {hasReferences && (
                    <section className="talk-section talk-references">
                        <h2 className="section-title">References</h2>
                        {(() => {
                            const parts = [];
                            if (talk?.references) parts.push(talk.references);
                            if (Array.isArray(talk?.referencesBodies) && talk.referencesBodies.length > 0) {
                                talk.referencesBodies.forEach((p) => { if (p) parts.push(p); });
                            }
                            const combinedHTML = mergeAndNormalizeHTML(parts);
                            return (
                                <div
                                    className="section-content"
                                    dangerouslySetInnerHTML={{ __html: combinedHTML }}
                                />
                            );
                        })()}
                    </section>
                )}
                </div>
                )}
            </main>
            <Footer />
        </div>
    );
}